import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import createEventEmitter from 'util/createEventEmitter';
import { closeHeaderEmitter, media } from './headerHelpers';

type CustomMenu = {
  id: string;
  parent: HTMLElement;
  element: React.ReactElement;
};

export const setCustomHeaderMenuEmitter = createEventEmitter<CustomMenu | null>();
export const cutstomHeaderMenuChangedEmitter = createEventEmitter<string | null>();

/**
 * Additional overlay which displays as a popup on desktop or drawer on smaller screens.
 * The component is overengineered because it's supposed to be reused for notifications too.
 */
const CustomHeaderMenu: React.FC = () => {
  const [currentMenu, setCurrentMenu] = useState<{
    menu: CustomMenu;
    style: {
      '--popup-top': string;
      '--popup-right': string;
    } & React.CSSProperties;
  } | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const getStyle = (parent: HTMLElement) => {
    const bbox = parent.getBoundingClientRect();
    const windowWidthWithoutScrollbar = Math.min(document.body.scrollWidth, window.innerWidth);

    return {
      '--popup-top': `${bbox.bottom}px`,
      '--popup-right': `${windowWidthWithoutScrollbar - bbox.right}px`,
    };
  };

  useEffect(() => {
    const removeChangeListener = setCustomHeaderMenuEmitter.addListener((newMenu) => {
      if (!newMenu) {
        setCurrentMenu(null);
        cutstomHeaderMenuChangedEmitter.emitEvent(null);
        return;
      }

      setCurrentMenu({
        menu: newMenu,
        style: getStyle(newMenu.parent),
      });
      cutstomHeaderMenuChangedEmitter.emitEvent(newMenu.id);
      closeHeaderEmitter.emitEvent();
    });

    return () => {
      removeChangeListener();
    };
  }, []);

  useEffect(() => {
    if (!currentMenu) {
      return;
    }

    const abortController = new AbortController();

    const handleClickOutsideOverlay = (ev: MouseEvent) => {
      if (overlayRef.current?.contains(ev.target as Node) || currentMenu.menu.parent.contains(ev.target as Node)) {
        return;
      }

      setCurrentMenu(null);
      cutstomHeaderMenuChangedEmitter.emitEvent(null);
    };
    document.addEventListener('click', handleClickOutsideOverlay, { signal: abortController.signal });

    const handleResize = () => {
      setCurrentMenu((old) => (old ? { ...old, style: getStyle(old.menu.parent) } : null));
    };
    window.addEventListener('resize', handleResize, { signal: abortController.signal });

    return () => abortController.abort();
  }, [currentMenu]);

  if (!currentMenu) {
    return null;
  }

  return (
    <Overlay ref={overlayRef} style={currentMenu.style}>
      {currentMenu.menu.element}
    </Overlay>
  );
};

export default CustomHeaderMenu;

const Overlay = styled.div`
  @media ${media.TABLET_OR_MORE} {
    position: fixed;
    top: var(--popup-top);
    right: var(--popup-right);
    min-width: 300px;
    padding: 8px;
    background-color: #ffffff;
    box-shadow: 0 8px 20px 0 rgba(0, 0, 0, 0.15);
    border-radius: 4px;

    transform-origin: top;
    transform: scaleY(100%);
    opacity: 1;
    transition: transform 100ms ease-out, opacity 300ms;
    @starting-style {
      transform: scaleY(0);
      opacity: 0;
    }
  }

  @media ${media.LESS_THAN_TABLET} {
    position: fixed;
    top: var(--header-height);
    left: 0;
    right: 0;
    background-color: #ffffff;
    box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.05);
    border-top: 1px solid ${(props) => props.theme.colors.elementsInputBorder};

    transform: translateX(0);
    transition: transform 100ms ease-out;
    @starting-style {
      transform: translateX(100dvw);
    }
  }
`;
